// Shared period/series helpers for the KPI reports. Every report shows
// MIN_YEAR onwards only (older years are sparse / pre-money in the sheet).

import { SOURCE_LABEL } from '../../orders/constants';

export const MIN_YEAR = 2020;

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// What to measure. Revenue is money (and carries a postage "basis"); orders/units
// are counts. Each get() reads a stored metrics object {orders,units,revenue,
// netSales,postage}. `money` drives £ vs count formatting.
export const MEASURES = {
  revenue: { label: 'Revenue', money: true },
  orders: { label: 'Orders', money: false, get: (m) => Number(m?.orders) || 0 },
  units: { label: 'Units', money: false, get: (m) => Number(m?.units) || 0 },
};

// Revenue is split into independently-toggleable parts. Selecting both = gross,
// net alone = items only, postage alone = postage. At least one is always on.
export const REVENUE_PARTS = {
  net: { label: 'Net', field: 'netSales' },
  postage: { label: 'Postage', field: 'postage' },
};
export const DEFAULT_PARTS = ['net', 'postage'];

export const isMoneyMeasure = (measure) => !!MEASURES[measure]?.money;

/**
 * Accessor over a stored metrics object for the chosen measure. For revenue,
 * `parts` is the list of included components (['net','postage']).
 */
export function metricAccessor(measure, parts = DEFAULT_PARTS) {
  if (measure === 'revenue') {
    const useNet = parts.includes('net');
    const usePostage = parts.includes('postage');
    return (m) => (useNet ? Number(m?.netSales) || 0 : 0) + (usePostage ? Number(m?.postage) || 0 : 0);
  }
  return (MEASURES[measure] || MEASURES.orders).get;
}

export const yearOf = (key) => key.slice(0, 4);

// ----- Year basis: calendar (Jan–Dec) or fiscal (1 Apr – 31 Mar) -----
// Fiscal year is computed as its own grouping: months Apr–Dec belong to FY
// starting that calendar year; Jan–Mar belong to the FY that started the prior
// year. Labelled "2024/25". Quarters/months re-index so April = 1.
export const YEAR_BASIS = { calendar: 'Calendar (Jan–Dec)', fiscal: 'Financial (Apr–Mar)' };
const fyKey = (start) => `${start}/${String(start + 1).slice(2)}`; // 2024 -> "2024/25"
// Numeric sort/colour key (start year), works for both "2024" and "2024/25".
export const yearNum = (y) => Number(String(y).slice(0, 4));

/** Year-group key for a monthly key 'YYYY-MM' under the chosen basis. */
export function monthYearKey(mk, basis) {
  const y = Number(mk.slice(0, 4));
  const mo = Number(mk.slice(5, 7));
  if (basis === 'fiscal') return fyKey(mo >= 4 ? y : y - 1);
  return String(y);
}
// { year, idx } for a monthly key: idx is the month position within the year
// (calendar Jan=1…Dec=12; fiscal Apr=1…Mar=12).
function monthMeta(mk, basis) {
  const y = Number(mk.slice(0, 4));
  const mo = Number(mk.slice(5, 7));
  if (basis === 'fiscal') return { year: fyKey(mo >= 4 ? y : y - 1), idx: ((mo - 4 + 12) % 12) + 1 };
  return { year: String(y), idx: mo };
}
// { year, idx } for an ISO weekly key 'YYYY-Www'. Fiscal weeks are approximate
// (ISO weeks don't align to 1 Apr): weeks ≤13 fall in the prior FY, and the FY
// week index starts at ISO week 14 ≈ early April.
function weekMeta(wk, basis) {
  const y = Number(wk.slice(0, 4));
  const w = Number(wk.slice(6));
  if (basis === 'fiscal') return { year: fyKey(w <= 13 ? y - 1 : y), idx: ((w - 14 + 53) % 53) + 1 };
  return { year: String(y), idx: w };
}
const fiscalMonthLabel = (i) => MONTH_LABELS[(i + 2) % 12]; // fiscal idx 1 -> Apr
// Quarter label with its month span, which differs by basis:
// calendar Q1 = Jan–Mar; fiscal Q1 = Apr–Jun (… fiscal Q4 = Jan–Mar).
const quarterLabel = (i, basis) => {
  const first = (i - 1) * 3 + 1;
  const lab = basis === 'fiscal' ? fiscalMonthLabel : (k) => MONTH_LABELS[k - 1];
  return `Q${i} (${lab(first)}–${lab(first + 2)})`;
};

// Year-overlay chart colours: the latest ("current") year is solid black; each
// prior year takes a fixed palette slot by how many years back it is (so "last
// year" is always the same colour whatever range is shown), drawn fainter.
const PRIOR_YEAR_PALETTE = ['#1f77b4', '#ff7f0e', '#2ca02c', '#9467bd', '#8c564b', '#e377c2', '#17becf', '#bcbd22', '#d62728'];
const PRIOR_YEAR_ALPHA = 0.7;
function hexToRgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
/** Colour for `year` given the latest year on the chart (`maxYear`). Works for
 * calendar ("2024") and fiscal ("2024/25") labels via the leading start year. */
export function yearColour(year, maxYear) {
  const back = yearNum(maxYear) - yearNum(year);
  if (back <= 0) return '#000000'; // current/latest year
  return hexToRgba(PRIOR_YEAR_PALETTE[(back - 1) % PRIOR_YEAR_PALETTE.length], PRIOR_YEAR_ALPHA);
}
const monthIdx = (mk) => Number(mk.slice(5, 7)); // 'YYYY-MM' -> 1..12
const num = (v) => Number(v) || 0;
const inRange = (key) => Number(yearOf(key)) >= MIN_YEAR;

// Channel display order + labels (etsy/ebay/shopify/direct).
export const CHANNEL_ORDER = ['etsy', 'ebay', 'shopify', 'direct'];
export const channelLabel = (c) => SOURCE_LABEL[c] || c;

// Fixed per-channel colours so a channel is always the same colour across charts
// and its toggle chip matches its line/area.
const CHANNEL_COLOURS = { etsy: '#f1641e', ebay: '#0064d2', shopify: '#5e8e3e', direct: '#6b7280' };
export const channelColour = (c) => CHANNEL_COLOURS[c] || '#9aa0a6';

// Granularity config: which stats map to read, and how to derive a period's
// { year, idx } and label under the chosen year basis.
export const GRANULARITIES = {
  week: {
    label: 'Week',
    source: 'weekly',
    meta: (k, basis) => weekMeta(k, basis),
    indices: () => range(1, 53),
    idxLabel: (i) => `W${i}`,
  },
  month: {
    label: 'Month',
    source: 'monthly',
    meta: (k, basis) => monthMeta(k, basis),
    indices: () => range(1, 12),
    idxLabel: (i, basis) => (basis === 'fiscal' ? fiscalMonthLabel(i) : MONTH_LABELS[i - 1]),
  },
  quarter: {
    label: 'Quarter',
    source: 'monthly',
    meta: (k, basis) => {
      const mm = monthMeta(k, basis);
      return { year: mm.year, idx: Math.ceil(mm.idx / 3) };
    },
    indices: () => [1, 2, 3, 4],
    idxLabel: (i, basis) => quarterLabel(i, basis),
  },
};

function range(a, b) {
  const out = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
}

/**
 * Year-on-year matrix at a given granularity. `get(metricsObj)` extracts the
 * value (top-level buckets are themselves metrics objects).
 * Returns { years:[...], indices:[...], idxLabel, valueAt(year, idx) } where
 * indices are only those actually present (so week charts don't pad to 53).
 */
export function yoyMatrix(stats, granularity, get, basis = 'calendar') {
  const g = GRANULARITIES[granularity];
  const map = (stats && stats[g.source]) || {};
  const years = new Set();
  const present = new Set();
  const data = {}; // year -> idx -> value
  Object.entries(map).forEach(([k, doc]) => {
    if (!inRange(k)) return;
    const { year, idx } = g.meta(k, basis);
    years.add(year);
    present.add(idx);
    (data[year] || (data[year] = {}))[idx] = (data[year][idx] || 0) + num(get(doc));
  });
  const indices = g.indices(basis).filter((i) => present.has(i));
  return {
    years: [...years].sort(),
    indices,
    idxLabel: (i) => g.idxLabel(i, basis),
    valueAt: (year, idx) => data[year]?.[idx] || 0,
  };
}

/**
 * Per-channel YoY (always monthly): channel -> year -> monthIdx -> metric value.
 * Used for the small-multiples report.
 */
export function channelYoY(stats, get, basis = 'calendar') {
  const map = (stats && stats.monthly) || {};
  const channels = new Set();
  const data = {};
  Object.entries(map).forEach(([mk, doc]) => {
    if (!inRange(mk)) return;
    const { year, idx } = monthMeta(mk, basis);
    Object.entries(doc.byChannel || {}).forEach(([c, m]) => {
      channels.add(c);
      const dc = data[c] || (data[c] = {});
      const dy = dc[year] || (dc[year] = {});
      dy[idx] = (dy[idx] || 0) + num(get(m));
    });
  });
  const ordered = [...CHANNEL_ORDER.filter((c) => channels.has(c)), ...[...channels].filter((c) => !CHANNEL_ORDER.includes(c))];
  return {
    channels: ordered,
    years: [...new Set(Object.values(data).flatMap((dc) => Object.keys(dc)))].sort(),
    valueAt: (channel, year, idx) => data[channel]?.[year]?.[idx] || 0,
    idxLabel: (i) => (basis === 'fiscal' ? fiscalMonthLabel(i) : MONTH_LABELS[i - 1]),
  };
}

/**
 * Continuous monthly series by channel (chronological), for the trend/stacked-area
 * report. Returns { months:['YYYY-MM'...], labels:['Jan 20'...], series:[{channel,label,data[]}] }.
 */
export function continuousByChannel(stats, get) {
  const map = (stats && stats.monthly) || {};
  const months = Object.keys(map).filter(inRange).sort();
  const channels = new Set();
  months.forEach((mk) => Object.keys(map[mk].byChannel || {}).forEach((c) => channels.add(c)));
  const ordered = [...CHANNEL_ORDER.filter((c) => channels.has(c)), ...[...channels].filter((c) => !CHANNEL_ORDER.includes(c))];
  const series = ordered.map((c) => ({
    channel: c,
    label: channelLabel(c),
    data: months.map((mk) => num(get(map[mk].byChannel?.[c] || {}))),
  }));
  const labels = months.map((mk) => `${MONTH_LABELS[monthIdx(mk) - 1]} ${mk.slice(2, 4)}`);
  return { months, labels, series };
}
